import { useEffect, useState } from "react";

import { useFormik } from "formik";
import palette from "../../../theme/palette";
import i18next from "i18next";
import moment from "moment";
import { BiInfoCircle, BiWallet } from "react-icons/bi";
import { LuCheckCircle, LuCreditCard } from "react-icons/lu";
import { RiWallet3Line } from "react-icons/ri";
import { useSelector } from "react-redux";
import * as Yup from "yup";

import { useAuth } from "../../../context/auth";
import { getPaymentSystems, getTransactions, rechargeBalance } from "../../../protocols";
import { normalizePrice } from "../../../utils";
import { ContainerComponent, DotsLoadingComponent } from "../../ui";

import {
  AmountInput,
  AmountInputWrapper,
  AmountSuffix,
  BalanceAmount,
  BalanceCard,
  BalanceCardContent,
  BalanceCardIcon,
  BalanceLabel,
  ContentCard,
  CreditLimitText,
  ErrorText,
  InfoCard,
  InfoCardIcon,
  InfoCardTitle,
  InfoItem,
  InfoList,
  LoadingWrapper,
  MainContent,
  PageSubtitle,
  PageTitle,
  PaymentMethodCard,
  PaymentMethodLogo,
  PaymentMethodName,
  PaymentMethodsGrid,
  QuickAmountButton,
  QuickAmountsGrid,
  QuickAmountsLabel,
  RecentRechargesCard,
  RecentRechargesTitle,
  RechargeAmount,
  RechargeDate,
  RechargeItem,
  RechargeStatus,
  SectionIcon,
  SectionTitle,
  Sidebar,
  SubmitButton,
  TwoColumnLayout,
  Wrapper,
  CommissionQuote,
} from "./style";

const BalanceRechargeComponent = () => {
  const { language, t } = i18next;

  const { user, refreshUser } = useAuth();
  const { currency } = useSelector(state => state.appConfigs);

  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  // комиссия платёжной системы для введённой суммы: { commission, total } (считает бек по amount)
  const [commissionQuote, setCommissionQuote] = useState(null);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [recentRecharges, setRecentRecharges] = useState([]);
  const [isLoadingRecharges, setIsLoadingRecharges] = useState(true);

  const quickAmounts = [1];

  useEffect(() => {
    const fetchPaymentSystems = async () => {
      const result = await getPaymentSystems("balance");
      if (result?.length) {
        setPaymentMethods(result);
        setPaymentMethod(result[0]?.id);
      }
    };

    const fetchRecentRecharges = async () => {
      try {
        const result = await getTransactions({ type_operation: "balance", limit: 5 });
        setRecentRecharges(result?.data?.data || []);
      } catch (error) {
        console.error("Error fetching recharges:", error);
      } finally {
        setIsLoadingRecharges(false);
      }
    };

    fetchPaymentSystems();
    fetchRecentRecharges();
  }, []);

  const validationSchema = Yup.object().shape({
    amount: Yup.number()
      .typeError(t("only_numbers"))
      .test("minAmount", `${t("min_number")} 1 ${user?.currency || currency}`, val => val >= 1)
      .test("maxAmount", `${t("max_number")} 500000 ${user?.currency || currency}`, val => val <= 500000)
      .test("not_number", t("only_numbers"), value =>
        /(?:^[1-9]([0-9]+)?(?:\.[0-9]{1,2})?$)|(?:^(?:0)$)|(?:^[0-9]\.[0-9](?:[0-9])?$)/.test(value)
      )
      .required(t("required")),
  });

  const chargeBalance = async values => {
    setIsPaymentLoading(true);

    await rechargeBalance({
      amount: values.amount,
      payment_systems_id: paymentMethod,
      return_url: window.location.origin,
      fail_url: window.location.origin,
    })
      .then(res => {
        const { pay_url } = res.data || {};

        if (pay_url) {
          window.location.href = pay_url;
        }
      })
      .finally(() => {
        setIsPaymentLoading(false);
      });
  };

  const { values, errors, touched, handleBlur, handleSubmit, setFieldValue } = useFormik({
    initialValues: {
      amount: "",
    },
    validationSchema,
    validateOnBlur: true,
    onSubmit: chargeBalance,
  });

  useEffect(() => {
    const amount = Number(values.amount);
    if (!paymentMethod || !(amount > 0)) {
      setCommissionQuote(null);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await getPaymentSystems("balance", null, {}, amount);
        const method = result?.find(item => item.id === paymentMethod);
        if (!cancelled) {
          setCommissionQuote(
            method ? { commission: Number(method.commission_value) || 0, total: Number(method.amount) || amount } : null,
          );
        }
      } catch {
        if (!cancelled) setCommissionQuote(null);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [values.amount, paymentMethod]);

  const handleOnClickItem = id => {
    if (id !== paymentMethod) {
      setPaymentMethod(id);
    }
  };

  const handleQuickAmount = amount => {
    setFieldValue("amount", amount);
  };

  const formatDate = dateString => {
    return moment(dateString).format("DD.MM.YYYY");
  };

  return (
    <Wrapper>
      <ContainerComponent>
        <PageTitle>{t("recharge_balance")}</PageTitle>
        <PageSubtitle>{t("add_funds_securely")}</PageSubtitle>

        <TwoColumnLayout>
          <MainContent>
            {/* Current Balance Card */}
            <BalanceCard>
              <BalanceCardIcon>
                <RiWallet3Line size={24} />
              </BalanceCardIcon>
              <BalanceCardContent>
                <BalanceLabel>{t("current_balance")}</BalanceLabel>
                <BalanceAmount>
                  {normalizePrice(user?.balance || 0)} {user?.currency || currency}
                </BalanceAmount>
                <CreditLimitText>
                  {t("credit_limit")}: {normalizePrice(user?.credit_limit || 0)} {user?.currency || currency}
                </CreditLimitText>
              </BalanceCardContent>
            </BalanceCard>

            {/* Payment Methods */}
            <ContentCard>
              <SectionTitle>
                <SectionIcon>
                  <LuCreditCard size={20} />
                </SectionIcon>
                {t("select_payment_method")}
              </SectionTitle>

              <PaymentMethodsGrid>
                {paymentMethods?.map(method => (
                  <PaymentMethodCard
                    key={method.id}
                    $isActive={method.id === paymentMethod}
                    onClick={() => handleOnClickItem(method.id)}
                  >
                    <PaymentMethodLogo src={method.logo} alt={method.name} />
                    <PaymentMethodName>{method.name}</PaymentMethodName>
                  </PaymentMethodCard>
                ))}
              </PaymentMethodsGrid>
            </ContentCard>

            {/* Amount Input */}
            <ContentCard>
              <SectionTitle>{t("recharging_amount")}</SectionTitle>

              <AmountInputWrapper>
                <AmountInput
                  type="number"
                  value={values.amount}
                  onChange={e => setFieldValue("amount", e.target.value)}
                  onBlur={handleBlur("amount")}
                  placeholder="1"
                  $hasError={touched?.amount && errors?.amount}
                />
                <AmountSuffix>{user?.currency || currency}</AmountSuffix>
              </AmountInputWrapper>
              {touched?.amount && errors?.amount && <ErrorText>{errors.amount}</ErrorText>}

              <QuickAmountsLabel>{t("quick_amounts")}:</QuickAmountsLabel>
              <QuickAmountsGrid>
                {quickAmounts.map(amount => (
                  <QuickAmountButton
                    key={amount}
                    onClick={() => handleQuickAmount(amount)}
                    $isActive={values.amount == amount}
                  >
                    {normalizePrice(amount)}
                  </QuickAmountButton>
                ))}
              </QuickAmountsGrid>

              {commissionQuote?.commission > 0 && (
                <CommissionQuote>
                  <div>
                    <span>{t("payment_commission_fee")}</span>
                    <span>
                      + {normalizePrice(commissionQuote.commission)} {user?.currency || currency}
                    </span>
                  </div>
                  <div className="total">
                    <span>{t("payment_commission_to_pay")}</span>
                    <span>
                      {normalizePrice(commissionQuote.total)} {user?.currency || currency}
                    </span>
                  </div>
                </CommissionQuote>
              )}

              <SubmitButton
                onClick={handleSubmit}
                type="submit"
                disabled={isPaymentLoading || !paymentMethod || !values.amount}
              >
                {isPaymentLoading ? (
                  <LoadingWrapper>
                    <DotsLoadingComponent color={palette.white} />
                  </LoadingWrapper>
                ) : (
                  <>
                    <BiWallet size={20} />
                    {t("recharge")}{" "}
                    {values.amount ? `${normalizePrice(values.amount)} ${user?.currency || currency}` : ""}
                  </>
                )}
              </SubmitButton>
            </ContentCard>
          </MainContent>

          <Sidebar>
            {/* Important Information */}
            <InfoCard>
              <InfoCardTitle>
                <InfoCardIcon>
                  <BiInfoCircle size={20} />
                </InfoCardIcon>
                {t("important_information")}
              </InfoCardTitle>
              <InfoList>
                <InfoItem>
                  • {t("minimum_recharge")}: 1 {user?.currency || currency}
                </InfoItem>
                <InfoItem>
                  • {t("maximum_recharge")}: 500,000 {user?.currency || currency}
                </InfoItem>
                <InfoItem>
                  • {t("processing_time")}: {t("instant")}
                </InfoItem>
                <InfoItem>• {t("no_additional_fees")}</InfoItem>
              </InfoList>
            </InfoCard>

            {/* Recent Recharges */}
            <RecentRechargesCard>
              <RecentRechargesTitle>{t("recent_recharges")}</RecentRechargesTitle>

              {isLoadingRecharges ? (
                <LoadingWrapper>
                  <DotsLoadingComponent />
                </LoadingWrapper>
              ) : recentRecharges.length === 0 ? (
                <InfoItem style={{ textAlign: "center", color: palette.textSecondary }}>{t("no_recent_recharges")}</InfoItem>
              ) : (
                recentRecharges.slice(0, 5).map((recharge, index) => (
                  <RechargeItem key={index}>
                    <div>
                      <RechargeAmount>
                        {normalizePrice(recharge.amount)} {recharge.currency || user?.currency || currency}
                      </RechargeAmount>
                      <RechargeDate>{formatDate(recharge.created_at)}</RechargeDate>
                    </div>
                    <RechargeStatus
                      $success={recharge.payment_status === "success" || recharge.payment_status === "SUCCESS"}
                    >
                      {recharge.payment_status === "success" || recharge.payment_status === "SUCCESS" ? (
                        <>
                          <LuCheckCircle size={14} />
                          Success
                        </>
                      ) : (
                        recharge.payment_status
                      )}
                    </RechargeStatus>
                  </RechargeItem>
                ))
              )}
            </RecentRechargesCard>
          </Sidebar>
        </TwoColumnLayout>
      </ContainerComponent>
    </Wrapper>
  );
};

export default BalanceRechargeComponent;
